import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { api } from '@/lib/api';
import { useWebSocket } from './useWebSocket';
import type { WSEvent } from '@/lib/ws';
import { PAINT_BOOTH_QUERY_KEY, type PaintBoothData } from './usePaintBooth';

export interface DashboardKPIs {
  openToday: number;
  inExecution: number;
  waitingPayment: number;
  /** Nulos sem painel.faturamento. */
  revenueToday: number | null;
  receivableTotal: number;
  monthRevenue: number | null;
  avgTicket: number | null;
  avgExecutionTime: number;
  nps: number | null;
  avgRating: number | null;
  freeSlots: number;
  totalSlots: number;
}

export interface DashboardOrder {
  id: string;
  number: number;
  plate: string;
  vehicleId: string;
  vehicleName: string;
  mechanicName: string;
  status: string;
  deadline: string | null;
  createdAt: string;
}

export interface ChartDataPoint {
  date: string;
  in_analysis: number;
  in_execution: number;
  waiting_part: number;
  outsourced: number;
  completed: number;
  waiting_payment: number;
  cancelled: number;
}

interface DashboardData {
  kpis: DashboardKPIs;
  /** A oficina já abriu alguma OS, alguma vez. `recentOrders` é só o dia. */
  hasAnyOrder: boolean;
  /**
   * OS paradas, pelo critério do servidor (limiar da oficina ou do tipo da
   * OS). Opcional: painel e API sobem em deploys separados.
   */
  staleOsCount?: number;
  /** Etapas passadas da duração padrão (F3.7), até 10. Opcional pelo mesmo motivo. */
  overdueStages?: { id: string; number: number; plate: string; module: string; stage: string; expectedBy: string }[];
  overdueStageCount?: number;
  /** Funilaria esperando peça (F3.7) — informação, fora das OS paradas. */
  waitingPart?: { count: number; oldestSince: string | null };
  recentOrders: DashboardOrder[];
  chartData: ChartDataPoint[];
}

export function useDashboard(workshopId: string | null) {
  const queryClient = useQueryClient();

  const query = useQuery<DashboardData>({
    queryKey: ['dashboard'],
    queryFn: async () => {
      const res = await api.get('/workshops/my/dashboard');
      return res.data;
    },
    enabled: !!workshopId,
    refetchInterval: 60_000,
  });

  const handleWSEvent = useCallback(
    (event: WSEvent) => {
      if (
        event.event === 'os-status-changed' ||
        event.event === 'os-opened' ||
        event.event === 'os-deleted' ||
        event.event === 'slot-status-changed' ||
        // Mudar de etapa é movimento: a OS sai das paradas do "Requer atenção".
        event.event === 'os-stage-changed'
      ) {
        queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      }
      // A cabine de pintura (F3.5) é derivada da etapa e do status da OS.
      // Oficina sem funilaria (a maioria) não rebusca a cada troca de status.
      const booth = queryClient.getQueryData<PaintBoothData>(PAINT_BOOTH_QUERY_KEY);
      if (
        booth?.enabled &&
        (event.event === 'os-stage-changed' ||
          event.event === 'os-status-changed' ||
          event.event === 'os-deleted')
      ) {
        queryClient.invalidateQueries({ queryKey: PAINT_BOOTH_QUERY_KEY });
      }
    },
    [queryClient],
  );

  const { status: wsStatus } = useWebSocket({
    workshopId,
    onEvent: handleWSEvent,
  });

  return {
    ...query,
    wsStatus,
  };
}
